import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import { ApiError } from "@/core/api/types";
import type {
  EnrichedIcmStatement,
  EnrichedIcmStatementDetail,
  IcmCellOverride,
  IcmLineItem,
  IcmJobsResponse,
  IcmStatement,
  IcmStatementDetail,
} from "./types";
import { enrichIcmStatement } from "./utils/enrich-statement";

const icmStatementsKey = (
  businessId?: string | null,
  icmRunId?: string | null,
) => ["icm-statements", businessId, icmRunId] as const;

const icmStatementsBaseKey = (businessId?: string | null) =>
  ["icm-statements", businessId] as const;

const icmJobsKey = (businessId?: string | null, icmRunId?: string | null) =>
  ["icm-jobs", businessId, icmRunId] as const;

const icmJobsBaseKey = (businessId?: string | null) =>
  ["icm-jobs", businessId] as const;

const icmStatementDetailKey = (
  businessId?: string | null,
  statementId?: string,
) => ["icm-statement-detail", businessId, statementId] as const;

const invalidateIcmOverrideQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  businessId?: string | null,
  statementId?: string,
) => {
  queryClient.invalidateQueries({
    queryKey: icmStatementDetailKey(businessId, statementId),
  });
  queryClient.invalidateQueries({
    queryKey: icmStatementsBaseKey(businessId),
  });
  queryClient.invalidateQueries({
    queryKey: icmJobsBaseKey(businessId),
  });
};

export const useGetIcmStatements = (
  businessId?: string | null,
  icmRunId?: string | null,
) => {
  const api = useApi();
  return useQuery({
    queryKey: icmStatementsKey(businessId, icmRunId),
    queryFn: (): Promise<EnrichedIcmStatement[]> =>
      api
        .get(`/businesses/${businessId}/icm/runs/${icmRunId}/statements`)
        .then((response) =>
          (response.data as IcmStatement[]).map(enrichIcmStatement),
        )
        .catch((error) => {
          if (error instanceof ApiError && error.status === 404) {
            return [];
          }
          throw error;
        }),
    enabled: !!businessId && !!icmRunId,
    placeholderData: (prev) => prev,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 3;
    },
  });
};

export const useGetIcmJobs = (
  businessId?: string | null,
  icmRunId?: string | null,
) => {
  const api = useApi();
  return useQuery({
    queryKey: icmJobsKey(businessId, icmRunId),
    queryFn: (): Promise<IcmJobsResponse> =>
      api
        .get(`/businesses/${businessId}/icm/runs/${icmRunId}/jobs`)
        .then((response) => response.data),
    enabled: !!businessId && !!icmRunId,
    placeholderData: (prev) => prev,
  });
};

export const useGetIcmStatementDetail = (
  businessId?: string | null,
  statementId?: string,
) => {
  const api = useApi();
  return useQuery({
    queryKey: icmStatementDetailKey(businessId, statementId),
    queryFn: (): Promise<EnrichedIcmStatementDetail> =>
      api
        .get(`/businesses/${businessId}/icm/statements/${statementId}`)
        .then((response) =>
          enrichIcmStatement(response.data as IcmStatementDetail),
        ),
    enabled: !!businessId && !!statementId,
  });
};

interface IcmCellOverrideMutationResponse {
  override: IcmCellOverride;
  line_item: Pick<IcmLineItem, "id" | "payout_amount" | "calculation_trace">;
  statement: Pick<IcmStatement, "id" | "status" | "total_payout">;
}

const icmCellOverridesKey = (
  businessId?: string | null,
  statementId?: string,
) => ["icm-cell-overrides", businessId, statementId] as const;

export const useGetIcmCellOverrides = (
  businessId?: string | null,
  statementId?: string,
) => {
  const api = useApi();
  return useQuery({
    queryKey: icmCellOverridesKey(businessId, statementId),
    queryFn: (): Promise<IcmCellOverride[]> =>
      api
        .get(`/icm/statements/${statementId}/cell_overrides`)
        .then((response) => response.data.overrides),
    enabled: !!businessId && !!statementId,
  });
};

export const useCreateIcmCellOverride = (
  businessId?: string | null,
  statementId?: string,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    IcmCellOverrideMutationResponse,
    Error,
    {
      payout_rule_id: string;
      source_record_id: string;
      column_name: string;
      value: string;
      reason: string;
    }
  >({
    mutationFn: (payload) =>
      api
        .post(`/icm/statements/${statementId}/cell_overrides`, payload)
        .then((res) => res.data),
    onSuccess: (data) => {
      queryClient.setQueryData<EnrichedIcmStatementDetail>(
        icmStatementDetailKey(businessId, statementId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            status: data.statement.status,
            total_payout: data.statement.total_payout,
            line_items: old.line_items.map((li) =>
              li.id === data.line_item.id ? { ...li, ...data.line_item } : li,
            ),
          };
        },
      );
      queryClient.setQueryData<IcmCellOverride[]>(
        icmCellOverridesKey(businessId, statementId),
        (old = []) => {
          const filtered = old.filter(
            (o) =>
              !(
                o.source_record_id === data.override.source_record_id &&
                o.column_name === data.override.column_name
              ),
          );
          return [...filtered, data.override];
        },
      );
      invalidateIcmOverrideQueries(queryClient, businessId, statementId);
    },
  });
};

export const useClearIcmCellOverride = (
  businessId?: string | null,
  statementId?: string,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    IcmCellOverrideMutationResponse,
    Error,
    { overrideId: string; reason?: string }
  >({
    mutationFn: ({ overrideId, reason }) =>
      api
        .delete(`/icm/statements/${statementId}/cell_overrides/${overrideId}`, {
          data: reason ? { reason } : undefined,
        })
        .then((res) => res.data),
    onSuccess: (data) => {
      queryClient.setQueryData<EnrichedIcmStatementDetail>(
        icmStatementDetailKey(businessId, statementId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            status: data.statement.status,
            total_payout: data.statement.total_payout,
            line_items: old.line_items.map((li) =>
              li.id === data.line_item.id ? { ...li, ...data.line_item } : li,
            ),
          };
        },
      );
      queryClient.setQueryData<IcmCellOverride[]>(
        icmCellOverridesKey(businessId, statementId),
        (old = []) =>
          old.filter(
            (o) =>
              !(
                o.source_record_id === data.override.source_record_id &&
                o.column_name === data.override.column_name
              ),
          ),
      );
      invalidateIcmOverrideQueries(queryClient, businessId, statementId);
    },
  });
};
